/** §2.4 visible range query and §2.10 marquee/keyboard helpers over a Layout. */
import type { Layout } from './layout';

/** Items intersecting the band [top, bottom). `out` is reused to avoid per-frame allocation. */
export function visibleIndices(layout: Layout, top: number, bottom: number, out: number[]): number[] {
  out.length = 0;
  const { positions, colItems, cols } = layout;
  for (let c = 0; c < cols; c++) {
    const idx = colItems[c]!;
    let lo = 0;
    let hi = idx.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      const p = positions[idx[mid]!]!;
      if (p.y + p.h > top) hi = mid;
      else lo = mid + 1;
    }
    for (let k = lo; k < idx.length; k++) {
      const i = idx[k]!;
      const p = positions[i]!;
      if (p.y >= bottom) break;
      out.push(i);
    }
  }
  return out;
}

/** Items intersecting a rect in grid coordinates (marquee). */
export function indicesInRect(layout: Layout, x1: number, y1: number, x2: number, y2: number, out: number[] = []): number[] {
  out.length = 0;
  const left = Math.min(x1, x2);
  const right = Math.max(x1, x2);
  const top = Math.min(y1, y2);
  const bottom = Math.max(y1, y2);
  const { positions, colItems, cols, colWidth, gap } = layout;
  for (let c = 0; c < cols; c++) {
    const cx = c * (colWidth + gap);
    if (cx + colWidth < left || cx > right) continue;
    const idx = colItems[c]!;
    let lo = 0;
    let hi = idx.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      const p = positions[idx[mid]!]!;
      if (p.y + p.h > top) hi = mid;
      else lo = mid + 1;
    }
    for (let k = lo; k < idx.length; k++) {
      const i = idx[k]!;
      const p = positions[i]!;
      if (p.y >= bottom) break;
      out.push(i);
    }
  }
  return out;
}

/** Nearest item (by vertical centre) in column `col` to centre-y `cy`, via binary search. */
export function nearestInColumn(layout: Layout, col: number, cy: number): number {
  const idx = layout.colItems[col];
  if (!idx || idx.length === 0) return -1;
  let lo = 0;
  let hi = idx.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    const p = layout.positions[idx[mid]!]!;
    if (p.y + p.h / 2 < cy) lo = mid + 1;
    else hi = mid;
  }
  const cand = [idx[lo]!, idx[lo - 1]].filter((v): v is number => v !== undefined);
  let best = cand[0]!;
  let bestD = Infinity;
  for (const i of cand) {
    const p = layout.positions[i]!;
    const d = Math.abs(p.y + p.h / 2 - cy);
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  }
  return best;
}

/** Arrow-key navigation target (§2.10). Returns -1 when there is no move. */
export function neighborIndex(layout: Layout, from: number, dir: 'left' | 'right' | 'up' | 'down'): number {
  const n = layout.placedCount;
  if (n === 0) return -1;
  if (from < 0 || from >= n) return 0;
  if (dir === 'left') return from > 0 ? from - 1 : -1;
  if (dir === 'right') return from < n - 1 ? from + 1 : -1;
  const p = layout.positions[from]!;
  const idx = layout.colItems[p.col]!;
  const k = idx.indexOf(from);
  const target = dir === 'up' ? idx[k - 1] : idx[k + 1];
  return target === undefined ? -1 : target;
}
